import React, {useEffect, useState} from 'react';

/**
 * Точная копия стартового сплеша из public/index.html — бюст, название и
 * кольцо гексов. Показывается там, где приложение уже смонтировано, но ждет
 * данных для первого экрана (проверка авторизации в App.js): без него между
 * HTML-сплешем и главным меню мелькал голый текст «Загрузка...».
 *
 * Своего CSS у компонента намеренно нет: классы .app-splash* объявлены
 * инлайном в <style> внутри index.html (иначе сплеш не имел бы стилей до
 * загрузки бандла), этот <style> живет в <head> все время работы приложения,
 * поэтому здесь переиспользуются те же правила. Отсюда следствие: править вид
 * сплеша нужно в index.html, и оба места меняются согласованно.
 */

// Геометрия кольца — как в MenuDemoLoader: гексы flat-top 500x434 по
// окружности, первый строго сверху, далее с шагом 60° по часовой стрелке
const SPLASH_HEXES = [
  {src: '/img/hexes/road.1f281346.png', dx: 0, dy: -1},
  {src: '/img/hexes/quarter.f69660fa.png', dx: 0.866, dy: -0.5},
  {src: '/img/hexes/farm.699e6dfc.png', dx: 0.866, dy: 0.5},
  {src: '/img/hexes/barracks.efab12f7.png', dx: 0, dy: 1},
  {src: '/img/hexes/mine.ecae90cd.png', dx: -0.866, dy: 0.5},
  {src: '/img/hexes/ruins.65862e11.png', dx: -0.866, dy: -0.5},
];

const BUST_SRC = '/img/other/strategikon_bust.png';

// Обычно сервер отвечает за доли секунды. Дольше — уже повод сказать, что
// мы ждем именно его, а не застряли; через AUTH_CHECK_TIMEOUT_MS проверка
// сдастся, и App покажет экран «нет связи» с диагностикой
export const SLOW_SERVER_HINT_DELAY_MS = 6000;

// Начертание должно совпадать с объявленным в @font-face (normal), иначе
// браузер сочтет шрифт неподходящим и не станет его грузить/засчитывать
const SPLASH_TITLE_FONT = "normal 2rem 'Franklin Gothic Heavy'";

const checkIfFontReady = () => {
  try {
    return Boolean(document.fonts?.check(SPLASH_TITLE_FONT));
  } catch (error) {
    // Экзотический браузер без Font Loading API — показываем текст сразу
    return true;
  }
};

// Синхронная проверка кэша (как в LoadedImage): к моменту показа этого сплеша
// картинки почти всегда уже загружены HTML-версией, и без проверки они зря
// проходили бы через состояние «еще не готово» с лишним фейдом
const checkIfImageCached = (imageSrc) => {
  const probe = new Image();
  probe.src = imageSrc;
  return probe.complete;
};

/**
 * Тот же гейт готовности, что и у HTML-сплеша: картинка скрыта, пока не
 * загрузится целиком, иначе видно, как PNG прорисовывается сверху вниз.
 */
const AppSplash = () => {
  const [isBustReady, setIsBustReady] = useState(() => checkIfImageCached(BUST_SRC));
  const [readyHexCount, setReadyHexCount] = useState(
    () => SPLASH_HEXES.filter((hex) => checkIfImageCached(hex.src)).length
  );
  const isRingReady = readyHexCount >= SPLASH_HEXES.length;

  // Заголовок ждет шрифт: при font-display: swap текст иначе рисуется запасным
  // Arial и потом прыгает на фирменный
  const [isTitleReady, setIsTitleReady] = useState(checkIfFontReady);
  useEffect(() => {
    if (isTitleReady || !document.fonts?.load) {
      return;
    }
    let isCancelled = false;
    const showTitle = () => {
      if (!isCancelled) {
        setIsTitleReady(true);
      }
    };
    // Ошибка загрузки шрифта — тоже показываем (запасным начертанием), чтобы
    // заголовок не остался невидимым
    document.fonts.load(SPLASH_TITLE_FONT).then(showTitle, showTitle);
    return () => {
      isCancelled = true;
    };
  }, [isTitleReady]);

  const [isServerSlow, setIsServerSlow] = useState(false);
  useEffect(() => {
    const timeoutId = setTimeout(() => setIsServerSlow(true), SLOW_SERVER_HINT_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  // Ошибка загрузки считается завершением: лучше показать сломанную картинку,
  // чем оставить сплеш навсегда пустым
  const handleHexSettled = () => setReadyHexCount((count) => count + 1);

  return (
    <div className="app-splash">
      <img
        className={`app-splash_bust ${isBustReady ? 'app-splash_bust_ready' : ''}`}
        src={BUST_SRC}
        alt=""
        onLoad={() => setIsBustReady(true)}
        onError={() => setIsBustReady(true)}
      />
      <h1 className={`app-splash_title ${isTitleReady ? 'app-splash_title_ready' : ''}`}>
        Стратегикон
      </h1>
      <div className="app-splash_loader">
        {/* Кольцо появляется целиком: поочередное проявление гексов — часть
            самой анимации, показывать его недоукомплектованным незачем */}
        <div className={`app-splash_ring ${isRingReady ? 'app-splash_ring_ready' : ''}`}>
          {SPLASH_HEXES.map((hex, index) => (
            <img
              key={hex.src}
              className="app-splash_ring-hex"
              src={hex.src}
              alt=""
              style={{'--hex-index': index, '--hex-dx': hex.dx, '--hex-dy': hex.dy}}
              onLoad={handleHexSettled}
              onError={handleHexSettled}
            />
          ))}
        </div>
        {isServerSlow && (
          <div className="app-splash_status">
            <p className="app-splash_status-title">Сервер отвечает дольше обычного...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppSplash;
